import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    Container,
    Paper,
    Group,
    Stack,
    Title,
    Text,
    Tabs,
    Button,
    Table,
    Badge,
    ActionIcon,
    Modal,
    TextInput,
    Select,
    NumberInput,
    Switch,
    CopyButton,
    Tooltip,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { IconPlus, IconPencil, IconTrash, IconCopy, IconBan } from "@tabler/icons-react";
import HeroSectionV2 from "../../../../components/HeroSectionV2";
import Billede1 from "../../../../Images/Billlede-1.png";
const RED = "#ff5e5e";
const apiUrl = process.env.REACT_APP_API_URL;

type TransportAdminEventDto = { eventId: number; name: string };
type TransportAdminPersonDto = { personId: number; name: string; email?: string | null };

type LocationDto = {
    id: number;
    eventId: number;
    nameDK: string;
    nameEN?: string | null;
    locationType: "pickup" | "dropoff" | "both" | string;
    sortOrder: number;
    isActive: boolean;
};

type TimeWindowDto = {
    id: number;
    eventId: number;
    date: string;
    startTime: string;
    endTime: string;
    intervalMinutes: number;
    bookingMaxDaysAhead: number;
    isActive: boolean;
};

type TokenDto = {
    id: number;
    token: string;
    eventId: number;
    personId: number;
    expiresAtUtc: string;
    isActive: boolean;
    personName?: string | null;
    email?: string | null;
};

type VehicleDto = {
    id: number;
    eventId: number;
    name: string;
    plate?: string | null;
    seats: number;
    isActive: boolean;
};

export default function TransportAdminPage() {
    const [events, setEvents] = useState<TransportAdminEventDto[]>([]);
    const [eventId, setEventId] = useState<number | null>(null);

    const [locations, setLocations] = useState<LocationDto[]>([]);
    const [windows, setWindows] = useState<TimeWindowDto[]>([]);
    const [tokens, setTokens] = useState<TokenDto[]>([]);
    const [vehicles, setVehicles] = useState<VehicleDto[]>([]);

    const [, setLoading] = useState(false);

    // Tabs
    const [activeTab, setActiveTab] = useState<string | null>("locations");

    // Location modal/state
    const [locModalOpen, setLocModalOpen] = useState(false);
    const [locEditing, setLocEditing] = useState<LocationDto | null>(null);
    const [locNameDK, setLocNameDK] = useState("");
    const [locNameEN, setLocNameEN] = useState("");
    const [locType, setLocType] = useState<"pickup" | "dropoff" | "both">("both");
    const [locSort, setLocSort] = useState<number>(0);
    const [locActive, setLocActive] = useState(true);

    // Time window modal/state
    const [winModalOpen, setWinModalOpen] = useState(false);
    const [winEditing, setWinEditing] = useState<TimeWindowDto | null>(null);
    const [winDate, setWinDate] = useState("");
    const [winStart, setWinStart] = useState("08:00");
    const [winEnd, setWinEnd] = useState("18:00");
    const [winInterval, setWinInterval] = useState(30);
    const [winMaxAhead, setWinMaxAhead] = useState(1);
    const [winActive, setWinActive] = useState(true);

    // Token modal/state
    const [tokenModalOpen, setTokenModalOpen] = useState(false);
    const [personQuery, setPersonQuery] = useState("");
    const [personResults, setPersonResults] = useState<TransportAdminPersonDto[]>([]);
    const [selectedPersonId, setSelectedPersonId] = useState<number | null>(null);
    const [expiresDays, setExpiresDays] = useState(30);

    // Vehicle modal/state
    const [vehicleModalOpen, setVehicleModalOpen] = useState(false);
    const [vehicleEditing, setVehicleEditing] = useState<VehicleDto | null>(null);
    const [vehicleName, setVehicleName] = useState("");
    const [vehicleRegistrationNumber, setVehicleRegistrationNumber] = useState("");
    const [vehicleSeats, setVehicleSeats] = useState(4);
    const [vehicleActive, setVehicleActive] = useState(true);

    useEffect(() => {
        (async () => {
            const res = await axios.get(`${apiUrl}/api/transport/admin/events`);
            setEvents(res.data);
            if (res.data?.length) {
                setEventId(res.data[0].eventId);
            }
        })();
    }, []);

    const eventOptions = useMemo(
        () => events.map((e) => ({ value: String(e.eventId), label: e.name })),
        [events]
    );

    const refreshAll = async (eid: number) => {
        setLoading(true);
        try {
            const loc = await axios.get(`${apiUrl}/api/transport/admin/locations`, {
                params: { eventId: eid },
            });

            const win = await axios.get(`${apiUrl}/api/transport/admin/timewindows`, {
                params: { eventId: eid },
            });

            const tok = await axios.get(`${apiUrl}/api/transport/admin/tokens`, {
                params: { eventId: eid, activeOnly: false },
            });

            setLocations(loc.data);
            setWindows(win.data);
            setTokens(tok.data);

            try {
                const veh = await axios.get(`${apiUrl}/api/transport/admin/vehicles`, {
                    params: { eventId: eid, activeOnly: false },
                });
                setVehicles(veh.data);
            } catch {
                setVehicles([]);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (eventId) {
            refreshAll(eventId);
        }
    }, [eventId]);

    useEffect(() => {
        const q = personQuery.trim();

        if (!eventId || q.length < 2) {
            setPersonResults([]);
            return;
        }

        const t = setTimeout(async () => {
            const res = await axios.get(`${apiUrl}/api/transport/admin/persons/search`, {
                params: { eventId, q },
            });
            setPersonResults(res.data);
        }, 250);

        return () => clearTimeout(t);
    }, [personQuery, eventId]);

    const bookingLink = (token: string) => `${window.location.origin}/transport/book?token=${token}`;

    const onTabChange = async (tab: string | null) => {
        setActiveTab(tab);
        if (eventId) {
            await refreshAll(eventId);
        }
    };

    // --------------------
    // Locations
    // --------------------
    const openCreateLocation = () => {
        setLocEditing(null);
        setLocNameDK("");
        setLocNameEN("");
        setLocType("both");
        setLocSort(0);
        setLocActive(true);
        setLocModalOpen(true);
    };

    const openEditLocation = (l: LocationDto) => {
        setLocEditing(l);
        setLocNameDK(l.nameDK);
        setLocNameEN(l.nameEN ?? "");
        setLocType((l.locationType as "pickup" | "dropoff" | "both") ?? "both");
        setLocSort(l.sortOrder);
        setLocActive(l.isActive);
        setLocModalOpen(true);
    };

    const saveLocation = async () => {
        if (!eventId) return;

        if (!locNameDK.trim()) {
            showNotification({ title: "Mangler", message: "NameDK er påkrævet" });
            return;
        }

        if (!locEditing) {
            await axios.post(`${apiUrl}/api/transport/admin/locations`, {
                eventId,
                nameDK: locNameDK,
                nameEN: locNameEN || null,
                locationType: locType,
                sortOrder: locSort,
            });
            showNotification({ title: "Gemt", message: "Sted oprettet" });
        } else {
            await axios.put(`${apiUrl}/api/transport/admin/locations/${locEditing.id}`, {
                nameDK: locNameDK,
                nameEN: locNameEN || null,
                locationType: locType,
                sortOrder: locSort,
                isActive: locActive,
            });
            showNotification({ title: "Gemt", message: "Sted opdateret" });
        }

        setLocModalOpen(false);
        await refreshAll(eventId);
    };

    const disableLocation = async (id: number) => {
        if (!eventId) return;

        await axios.delete(`${apiUrl}/api/transport/admin/locations/${id}`);
        showNotification({ title: "OK", message: "Sted deaktiveret" });
        await refreshAll(eventId);
    };

    // --------------------
    // Time windows
    // --------------------
    const openCreateWindow = () => {
        setWinEditing(null);
        setWinDate("");
        setWinStart("08:00");
        setWinEnd("18:00");
        setWinInterval(30);
        setWinMaxAhead(1);
        setWinActive(true);
        setWinModalOpen(true);
    };

    const openEditWindow = (w: TimeWindowDto) => {
        setWinEditing(w);
        setWinDate(w.date);
        setWinStart(w.startTime);
        setWinEnd(w.endTime);
        setWinInterval(w.intervalMinutes);
        setWinMaxAhead(w.bookingMaxDaysAhead);
        setWinActive(w.isActive);
        setWinModalOpen(true);
    };

    const saveWindow = async () => {
        if (!eventId) return;

        if (!winDate) {
            showNotification({ title: "Mangler", message: "Dato er påkrævet (yyyy-MM-dd)" });
            return;
        }

        if (!winEditing) {
            await axios.post(`${apiUrl}/api/transport/admin/timewindows`, {
                eventId,
                date: winDate,
                startTime: winStart,
                endTime: winEnd,
                intervalMinutes: winInterval,
                bookingMaxDaysAhead: winMaxAhead,
            });
            showNotification({ title: "Gemt", message: "Dag/tider oprettet" });
        } else {
            await axios.put(`${apiUrl}/api/transport/admin/timewindows/${winEditing.id}`, {
                date: winDate,
                startTime: winStart,
                endTime: winEnd,
                intervalMinutes: winInterval,
                bookingMaxDaysAhead: winMaxAhead,
                isActive: winActive,
            });
            showNotification({ title: "Gemt", message: "Dag/tider opdateret" });
        }

        setWinModalOpen(false);
        await refreshAll(eventId);
    };

    const disableWindow = async (id: number) => {
        if (!eventId) return;

        await axios.delete(`${apiUrl}/api/transport/admin/timewindows/${id}`);
        showNotification({ title: "OK", message: "Dag/tider deaktiveret" });
        await refreshAll(eventId);
    };

    // --------------------
    // Tokens
    // --------------------
    const openCreateToken = () => {
        setPersonQuery("");
        setPersonResults([]);
        setSelectedPersonId(null);
        setExpiresDays(30);
        setTokenModalOpen(true);
    };

    const createToken = async () => {
        if (!eventId || !selectedPersonId) {
            showNotification({ title: "Mangler", message: "Vælg person" });
            return;
        }

        await axios.post(`${apiUrl}/api/transport/admin/tokens`, {
            eventId,
            personId: selectedPersonId,
            expiresInDays: expiresDays,
        });

        showNotification({ title: "OK", message: "Token oprettet/returneret" });
        setTokenModalOpen(false);
        await refreshAll(eventId);
    };

    const disableToken = async (id: number) => {
        if (!eventId) return;

        await axios.post(`${apiUrl}/api/transport/admin/tokens/${id}/disable`);
        showNotification({ title: "OK", message: "Token deaktiveret" });
        await refreshAll(eventId);
    };

    // --------------------
    // Vehicles
    // --------------------
    const openCreateVehicle = () => {
        setVehicleEditing(null);
        setVehicleName("");
        setVehicleRegistrationNumber("");
        setVehicleSeats(4);
        setVehicleActive(true);
        setVehicleModalOpen(true);
    };

    const openEditVehicle = (v: VehicleDto) => {
        setVehicleEditing(v);
        setVehicleName(v.name);
        setVehicleRegistrationNumber(v.plate ?? "");
        setVehicleSeats(v.seats);
        setVehicleActive(v.isActive);
        setVehicleModalOpen(true);
    };

    const saveVehicle = async () => {
        if (!eventId) return;

        if (!vehicleName.trim()) {
            showNotification({ title: "Mangler", message: "Navn er påkrævet" });
            return;
        }

        if (!vehicleEditing) {
            await axios.post(`${apiUrl}/api/transport/admin/vehicles`, {
                eventId,
                name: vehicleName,
                plate: vehicleRegistrationNumber || null,
                seats: vehicleSeats,
            });

            showNotification({ title: "Gemt", message: "Bil oprettet" });
        } else {
            await axios.put(`${apiUrl}/api/transport/admin/vehicles/${vehicleEditing.id}`, {
                name: vehicleName,
                plate: vehicleRegistrationNumber || null,
                seats: vehicleSeats,
                isActive: vehicleActive,
            });

            showNotification({ title: "Gemt", message: "Bil opdateret" });
        }

        setVehicleModalOpen(false);
        await refreshAll(eventId);
    };

    const disableVehicle = async (id: number) => {
        if (!eventId) return;

        await axios.delete(`${apiUrl}/api/transport/admin/vehicles/${id}`);
        showNotification({ title: "OK", message: "Bil deaktiveret" });
        await refreshAll(eventId);
    };

    return (
        <div>
            <HeroSectionV2
                titleEn="Transport"
                titleDa="Transport"
                descriptionEn="Setup for day transport"
                descriptionDa="Opsætnings dagstransport"
                imageSrc={Billede1}
            />

            <Container size="xl" py="xl">
                <Paper withBorder p="lg" radius="md">
                    <Group justify="space-between" align="flex-end">
                        <Stack gap={2}>
                            <Title order={3}>Transport Admin</Title>
                            <Text c="dimmed" size="sm">
                                Opret steder, dage/tider, tokens og biler til transport booking
                            </Text>
                        </Stack>

                        <Select
                            label="Vælg event"
                            data={eventOptions}
                            value={eventId ? String(eventId) : null}
                            onChange={(v) => setEventId(v ? Number(v) : null)}
                            searchable
                            w={320}
                        />
                    </Group>
                </Paper>

                <Paper withBorder p="lg" radius="md" mt="md">
                    <Tabs value={activeTab} onChange={onTabChange} color="red">
                        <Tabs.List>
                            <Tabs.Tab value="locations">Steder</Tabs.Tab>
                            <Tabs.Tab value="timewindows">Dage & tider</Tabs.Tab>
                            <Tabs.Tab value="tokens">Tokens</Tabs.Tab>
                            <Tabs.Tab value="vehicles">Biler</Tabs.Tab>
                        </Tabs.List>

                        <Tabs.Panel value="locations" pt="md">
                            <Group justify="space-between" mb="sm">
                                <Text fw={700}>Steder</Text>
                                <Button leftSection={<IconPlus size={16} />} onClick={openCreateLocation} disabled={!eventId}
                                    style={{ backgroundColor: RED }}
                                    styles={{
                                        root: {
                                            transition: "transform 120ms ease, box-shadow 120ms ease",
                                            "&:hover": {
                                                transform: "translateY(-1px)",
                                                boxShadow: "0 10px 18px rgba(255,94,94,0.25)",
                                                backgroundColor: RED,
                                            },
                                        },
                                    }}
                                >
                                    Opret sted
                                </Button>
                            </Group>

                            <Table.ScrollContainer minWidth={900}>
                                <Table striped highlightOnHover>
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th>Navn (DK)</Table.Th>
                                            <Table.Th>Type</Table.Th>
                                            <Table.Th>Sort</Table.Th>
                                            <Table.Th>Status</Table.Th>
                                            <Table.Th />
                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        {locations.map((l) => (
                                            <Table.Tr key={l.id}>
                                                <Table.Td>{l.nameDK}</Table.Td>
                                                <Table.Td>
                                                    <Badge variant="light">{l.locationType}</Badge>
                                                </Table.Td>
                                                <Table.Td>{l.sortOrder}</Table.Td>
                                                <Table.Td>
                                                    <Badge color={l.isActive ? "green" : "gray"} variant="light">
                                                        {l.isActive ? "Active" : "Inactive"}
                                                    </Badge>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Group justify="flex-end" gap="xs">
                                                        <Tooltip label="Rediger">
                                                            <ActionIcon variant="light" onClick={() => openEditLocation(l)}>
                                                                <IconPencil size={16} />
                                                            </ActionIcon>
                                                        </Tooltip>
                                                        <Tooltip label="Deaktiver">
                                                            <ActionIcon color="red" variant="light" onClick={() => disableLocation(l.id)}>
                                                                <IconTrash size={16} />
                                                            </ActionIcon>
                                                        </Tooltip>
                                                    </Group>
                                                </Table.Td>
                                            </Table.Tr>
                                        ))}
                                    </Table.Tbody>
                                </Table>
                            </Table.ScrollContainer>
                        </Tabs.Panel>

                        <Tabs.Panel value="timewindows" pt="md">
                            <Group justify="space-between" mb="sm">
                                <Text fw={700}>Dage & tider</Text>
                                <Button leftSection={<IconPlus size={16} />} onClick={openCreateWindow} disabled={!eventId}
                                    style={{ backgroundColor: RED }}
                                    styles={{
                                        root: {
                                            transition: "transform 120ms ease, box-shadow 120ms ease",
                                            "&:hover": {
                                                transform: "translateY(-1px)",
                                                boxShadow: "0 10px 18px rgba(255,94,94,0.25)",
                                                backgroundColor: RED,
                                            },
                                        },
                                    }}>
                                    Opret dag/tider
                                </Button>
                            </Group>

                            <Table.ScrollContainer minWidth={900}>
                                <Table striped highlightOnHover>
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th>Dato</Table.Th>
                                            <Table.Th>Fra</Table.Th>
                                            <Table.Th>Til</Table.Th>
                                            <Table.Th>Interval</Table.Th>
                                            <Table.Th>Max dage frem</Table.Th>
                                            <Table.Th>Status</Table.Th>
                                            <Table.Th />
                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        {windows.map((w) => (
                                            <Table.Tr key={w.id}>
                                                <Table.Td>{w.date}</Table.Td>
                                                <Table.Td>{w.startTime}</Table.Td>
                                                <Table.Td>{w.endTime}</Table.Td>
                                                <Table.Td>{w.intervalMinutes} min</Table.Td>
                                                <Table.Td>{w.bookingMaxDaysAhead}</Table.Td>
                                                <Table.Td>
                                                    <Badge color={w.isActive ? "green" : "gray"} variant="light">
                                                        {w.isActive ? "Active" : "Inactive"}
                                                    </Badge>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Group justify="flex-end" gap="xs">
                                                        <Tooltip label="Rediger">
                                                            <ActionIcon variant="light" onClick={() => openEditWindow(w)}>
                                                                <IconPencil size={16} />
                                                            </ActionIcon>
                                                        </Tooltip>
                                                        <Tooltip label="Deaktiver">
                                                            <ActionIcon color="red" variant="light" onClick={() => disableWindow(w.id)}>
                                                                <IconTrash size={16} />
                                                            </ActionIcon>
                                                        </Tooltip>
                                                    </Group>
                                                </Table.Td>
                                            </Table.Tr>
                                        ))}
                                    </Table.Tbody>
                                </Table>
                            </Table.ScrollContainer>
                        </Tabs.Panel>

                        <Tabs.Panel value="tokens" pt="md">
                            <Group justify="space-between" mb="sm">
                                <Text fw={700}>Tokens</Text>
                                <Button leftSection={<IconPlus size={16} />} onClick={openCreateToken} disabled={!eventId}
                                    style={{ backgroundColor: RED }}
                                    styles={{
                                        root: {
                                            transition: "transform 120ms ease, box-shadow 120ms ease",
                                            "&:hover": {
                                                transform: "translateY(-1px)",
                                                boxShadow: "0 10px 18px rgba(255,94,94,0.25)",
                                                backgroundColor: RED,
                                            },
                                        },
                                    }}>
                                    Opret token
                                </Button>
                            </Group>

                            <Table.ScrollContainer minWidth={1000}>
                                <Table striped highlightOnHover>
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th>Person</Table.Th>
                                            <Table.Th>Email</Table.Th>
                                            <Table.Th>Token</Table.Th>
                                            <Table.Th>Expires</Table.Th>
                                            <Table.Th>Status</Table.Th>
                                            <Table.Th />
                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        {tokens.map((t) => {
                                            const short = `${t.token?.slice(0, 8)}…`;
                                            const url = bookingLink(t.token);

                                            return (
                                                <Table.Tr key={t.id}>
                                                    <Table.Td>{t.personName ?? t.personId}</Table.Td>
                                                    <Table.Td>{t.email ?? "-"}</Table.Td>
                                                    <Table.Td>{short}</Table.Td>
                                                    <Table.Td>{new Date(t.expiresAtUtc).toLocaleString()}</Table.Td>
                                                    <Table.Td>
                                                        <Badge color={t.isActive ? "green" : "gray"} variant="light">
                                                            {t.isActive ? "Active" : "Inactive"}
                                                        </Badge>
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <Group justify="flex-end" gap="xs">
                                                            <CopyButton value={url}>
                                                                {({ copied, copy }) => (
                                                                    <Tooltip label={copied ? "Kopieret!" : "Kopiér link"}>
                                                                        <ActionIcon variant="light" onClick={copy}>
                                                                            <IconCopy size={16} />
                                                                        </ActionIcon>
                                                                    </Tooltip>
                                                                )}
                                                            </CopyButton>

                                                            <Tooltip label="Deaktiver token">
                                                                <ActionIcon color="red" variant="light" onClick={() => disableToken(t.id)}>
                                                                    <IconBan size={16} />
                                                                </ActionIcon>
                                                            </Tooltip>
                                                        </Group>
                                                    </Table.Td>
                                                </Table.Tr>
                                            );
                                        })}
                                    </Table.Tbody>
                                </Table>
                            </Table.ScrollContainer>
                        </Tabs.Panel>

                        <Tabs.Panel value="vehicles" pt="md">
                            <Group justify="space-between" mb="sm">
                                <Text fw={700}>Biler</Text>
                                <Button leftSection={<IconPlus size={16} />} onClick={openCreateVehicle} disabled={!eventId}
                                    style={{ backgroundColor: RED }}
                                    styles={{
                                        root: {
                                            transition: "transform 120ms ease, box-shadow 120ms ease",
                                            "&:hover": {
                                                transform: "translateY(-1px)",
                                                boxShadow: "0 10px 18px rgba(255,94,94,0.25)",
                                                backgroundColor: RED,
                                            },
                                        },
                                    }}>
                                    Opret bil
                                </Button>
                            </Group>

                            <Table.ScrollContainer minWidth={900}>
                                <Table striped highlightOnHover>
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th>Navn</Table.Th>
                                            <Table.Th>Reg.nr.</Table.Th>
                                            <Table.Th>Sæder</Table.Th>
                                            <Table.Th>Status</Table.Th>
                                            <Table.Th />
                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        {vehicles.map((v) => (
                                            <Table.Tr key={v.id}>
                                                <Table.Td>{v.name}</Table.Td>
                                                <Table.Td>{v.plate ?? "-"}</Table.Td>
                                                <Table.Td>{v.seats}</Table.Td>
                                                <Table.Td>
                                                    <Badge color={v.isActive ? "green" : "gray"} variant="light">
                                                        {v.isActive ? "Active" : "Inactive"}
                                                    </Badge>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Group justify="flex-end" gap="xs">
                                                        <Tooltip label="Rediger">
                                                            <ActionIcon variant="light" onClick={() => openEditVehicle(v)}>
                                                                <IconPencil size={16} />
                                                            </ActionIcon>
                                                        </Tooltip>
                                                        <Tooltip label="Deaktiver">
                                                            <ActionIcon color="red" variant="light" onClick={() => disableVehicle(v.id)}>
                                                                <IconTrash size={16} />
                                                            </ActionIcon>
                                                        </Tooltip>
                                                    </Group>
                                                </Table.Td>
                                            </Table.Tr>
                                        ))}
                                    </Table.Tbody>
                                </Table>
                            </Table.ScrollContainer>
                        </Tabs.Panel>
                    </Tabs>
                </Paper>

                <Modal
                    opened={locModalOpen}
                    onClose={() => setLocModalOpen(false)}
                    title={locEditing ? "Rediger sted" : "Opret sted"}
                    centered
                >
                    <Stack>
                        <TextInput label="Navn (DK)" value={locNameDK} onChange={(e) => setLocNameDK(e.currentTarget.value)} />
                        <TextInput label="Navn (EN)" value={locNameEN} onChange={(e) => setLocNameEN(e.currentTarget.value)} />

                        <Select
                            label="Type"
                            data={[
                                { value: "pickup", label: "pickup" },
                                { value: "dropoff", label: "dropoff" },
                                { value: "both", label: "both" },
                            ]}
                            value={locType}
                            onChange={(v) => setLocType((v as "pickup" | "dropoff" | "both") ?? "both")}
                        />

                        <NumberInput label="SortOrder" value={locSort} onChange={(v) => setLocSort(Number(v) || 0)} />

                        {locEditing && (
                            <Switch label="Aktiv" checked={locActive} onChange={(e) => setLocActive(e.currentTarget.checked)} />
                        )}

                        <Group justify="flex-end">
                            <Button variant="default" onClick={() => setLocModalOpen(false)}>
                                Annullér
                            </Button>
                            <Button onClick={saveLocation}>Gem</Button>
                        </Group>
                    </Stack>
                </Modal>

                <Modal
                    opened={winModalOpen}
                    onClose={() => setWinModalOpen(false)}
                    title={winEditing ? "Rediger dag/tider" : "Opret dag/tider"}
                    centered
                >
                    <Stack>
                        <TextInput label="Dato (yyyy-MM-dd)" value={winDate} onChange={(e) => setWinDate(e.currentTarget.value)} />

                        <Group grow>
                            <TextInput label="Start (HH:mm)" value={winStart} onChange={(e) => setWinStart(e.currentTarget.value)} />
                            <TextInput label="Slut (HH:mm)" value={winEnd} onChange={(e) => setWinEnd(e.currentTarget.value)} />
                        </Group>

                        <Group grow>
                            <NumberInput label="Interval (min)" value={winInterval} onChange={(v) => setWinInterval(Number(v) || 30)} min={5} />
                            <NumberInput label="Max dage frem" value={winMaxAhead} onChange={(v) => setWinMaxAhead(Number(v) || 1)} min={0} />
                        </Group>

                        {winEditing && (
                            <Switch label="Aktiv" checked={winActive} onChange={(e) => setWinActive(e.currentTarget.checked)} />
                        )}

                        <Group justify="flex-end">
                            <Button variant="default" onClick={() => setWinModalOpen(false)}>
                                Annullér
                            </Button>
                            <Button onClick={saveWindow}>Gem</Button>
                        </Group>
                    </Stack>
                </Modal>

                <Modal opened={tokenModalOpen} onClose={() => setTokenModalOpen(false)} title="Opret token" centered>
                    <Stack>
                        <TextInput
                            label="Søg person (fornavn/efternavn)"
                            value={personQuery}
                            onChange={(e) => setPersonQuery(e.currentTarget.value)}
                            placeholder="Skriv min. 2 tegn..."
                        />

                        <Select
                            label="Vælg person"
                            data={personResults.map((p) => ({
                                value: String(p.personId),
                                label: `${p.name} (${p.email ?? "-"})`,
                            }))}
                            value={selectedPersonId ? String(selectedPersonId) : null}
                            onChange={(v) => setSelectedPersonId(v ? Number(v) : null)}
                            searchable
                            nothingFoundMessage="Ingen resultater"
                        />

                        <NumberInput
                            label="Udløber om (dage)"
                            value={expiresDays}
                            onChange={(v) => setExpiresDays(Number(v) || 30)}
                            min={1}
                            max={365}
                        />

                        <Group justify="flex-end">
                            <Button variant="default" onClick={() => setTokenModalOpen(false)}>
                                Annullér
                            </Button>
                            <Button onClick={createToken}>Opret</Button>
                        </Group>
                    </Stack>
                </Modal>

                <Modal
                    opened={vehicleModalOpen}
                    onClose={() => setVehicleModalOpen(false)}
                    title={vehicleEditing ? "Rediger bil" : "Opret bil"}
                    centered
                >
                    <Stack>
                        <TextInput
                            label="Navn"
                            value={vehicleName}
                            onChange={(e) => setVehicleName(e.currentTarget.value)}
                        />

                        <TextInput
                            label="Registreringsnummer"
                            value={vehicleRegistrationNumber}
                            onChange={(e) => setVehicleRegistrationNumber(e.currentTarget.value)}
                        />

                        <NumberInput
                            label="Antal sæder"
                            value={vehicleSeats}
                            onChange={(v) => setVehicleSeats(Number(v) || 1)}
                            min={1}
                        />

                        {vehicleEditing && (
                            <Switch
                                label="Aktiv"
                                checked={vehicleActive}
                                onChange={(e) => setVehicleActive(e.currentTarget.checked)}
                            />
                        )}

                        <Group justify="flex-end">
                            <Button variant="default" onClick={() => setVehicleModalOpen(false)}>
                                Annullér
                            </Button>
                            <Button onClick={saveVehicle}>Gem</Button>
                        </Group>
                    </Stack>
                </Modal>
            </Container>
        </div>
    );
}